import { Dayjs } from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker'
import { FormControl } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'

import TextInput from './TextInput'

import { adapterLocaleMap } from '@/utils/locale'
import { useIsUS } from '@/hooks/useIsUS'

export type DatePickerInputProps = Omit<DatePickerProps<Dayjs, Dayjs>, 'renderInput' | 'value'> & {
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * If true, the input will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  /**
   * Callback fired when the input is blur.
   *
   * @param {object} event The event source of the callback.
   */
  /**
   * The value of the picker
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  value: Dayjs | null
  sx?: SxProps<Theme>
}

export function DatePickerInput(props: DatePickerInputProps) {
  const { error, fullWidth, helperText, name, onBlur, sx, ...rest } = props
  const {
    i18n: { language },
  } = useTranslation()

  const isUS = useIsUS()
  
  const dateFormat = isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY'

  return (
    <FormControl fullWidth={fullWidth} sx={sx}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocaleMap[language]}>
        <DatePicker
          {...rest}
          inputFormat={dateFormat}
          onError={console.log}
          OpenPickerButtonProps={{
            size: 'small',
          }}
          renderInput={(params) => (
            <TextInput
              {...params}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'datetime-input',
              }}
              fullWidth={fullWidth}
              helperText={helperText}
              name={name}
              onBlur={onBlur}
              error={params.error || error}
            />
          )}
        />
      </LocalizationProvider>
    </FormControl>
  )
}

export default DatePickerInput
